/**
 * Employer SAINT® landing-page configuration.
 *
 * Each employer gets one config object. To add a new employer:
 *   1. Add an entry below (e.g. `export const delta = { ... }`).
 *   2. Add it to the ALL_EMPLOYERS array.
 *   3. Import `makeEmployerRoutes` from "./routes" and spread it into
 *      CoreRoutes (see the American Airlines example already wired in).
 *
 * No code changes to the landing/thank-you components are needed — they
 * read everything from this config.
 */

export interface EmployerConfig {
  /** Full employer name shown to visitors (e.g. "American Airlines"). */
  name: string;
  /** Short, URL-safe slug (e.g. "american-airlines"). */
  slug: string;
  /** Landing page URL path. */
  landingPath: string;
  /** Thank-you page URL path. */
  thankYouPath: string;
  /** CRM / tracking form id used in dataLayer + tracking payload. */
  trackingId: string;
  /** Human-readable form name for reporting. */
  formName: string;
  /** Meta title (50–60 chars). */
  metaTitle: string;
  /** Meta description (120–155 chars). */
  metaDescription: string;
  /** Hero eyebrow label (shown as a pill badge). */
  eyebrow: string;
  /** Hero H1. */
  h1: string;
  /** Hero supporting paragraph. */
  heroSub: string;
  /**
   * Optional insurance carrier this employer's plan uses. Omit if the
   * page should stay carrier-neutral (benefits check for "your plan").
   */
  carrierFull?: string;
  carrierShort?: string;
  /** Custom field id for the "Who is this for?" CRM field (already registered). */
  cfWhoIsThisFor: string;
  /** Custom field id for the "Employer Name" CRM field (already registered). */
  cfEmployerName: string;
}

// Registered CRM custom field ids reused across employer landing pages.
const CF_WHO_IS_THIS_FOR = "MYQlMr3lrCpW6TonKqy8";
const CF_EMPLOYER_NAME = "Lo0LKbbbbzL5W37GsvVy";

export const americanAirlines: EmployerConfig = {
  name: "American Airlines",
  slug: "american-airlines",
  landingPath: "/american-airlines-saint",
  thankYouPath: "/thank-you/american-airlines-saint",
  trackingId: "fb-ads-aa-saint",
  formName: "SAINT® American Airlines Employee Opt-in",
  metaTitle: "SAINT® TMS for American Airlines Employees | Neuro Wellness",
  metaDescription:
    "An American Airlines employee? SAINT®, an FDA-cleared 5-day depression treatment, may be available to you. Free, confidential benefits check.",
  eyebrow: "For American Airlines employees & family",
  h1: "When depression hasn't lifted, there's still a way forward.",
  heroSub:
    "SAINT® is an FDA-cleared, five-day treatment for adults whose depression hasn't improved with antidepressants. We'll check your benefits and walk you through every step — confidentially, and at no obligation.",
  cfWhoIsThisFor: CF_WHO_IS_THIS_FOR,
  cfEmployerName: CF_EMPLOYER_NAME,
};

export const ALL_EMPLOYERS: EmployerConfig[] = [americanAirlines];
